import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ChartBar,
  ArrowLeft,
  MagnifyingGlass,
  Scissors,
  Funnel,
  Warning,
  CheckCircle,
  ArrowDown,
  ArrowUp,
  FileXls,
  FilePdf
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Toaster, toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIAS = [
  { value: 'guia-1', label: 'Guia 1 - Prod. Técnicos' },
  { value: 'guia-1b', label: 'Guia 1B - Tintas' },
  { value: 'guia-2', label: 'Guia 2 - Revenda' },
  { value: 'guia-3', label: 'Guia 3 - Uso Interno' },
  { value: 'guia-4', label: 'Guia 4 - Limpeza' },
  { value: 'guia-5', label: 'Guia 5 - Copa' },
  { value: 'guia-6', label: 'Guia 6 - Escritório' },
  { value: 'guia-7', label: 'Guia 7 - Descartáveis' },
  { value: 'guia-8', label: 'Guia 8 - Reforma' }
];

const getCategoriaLabel = (value) => {
  const cat = CATEGORIAS.find(c => c.value === value);
  return cat ? cat.label : value;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

export const EstoqueAtual = () => {
  const navigate = useNavigate();
  const [estoque, setEstoque] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [exporting, setExporting] = useState(null);

  const fetchEstoque = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pacman/estoque`);
      setEstoque(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estoque');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEstoque();
  }, [fetchEstoque]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams();
      if (filterCategoria !== 'all') params.set('categoria', filterCategoria);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (searchTerm) params.set('search', searchTerm);
      
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/pacman/estoque/export/${format}${qs}`, {
        responseType: 'blob'
      });
      
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const mime = format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        : 'application/pdf';
      
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `estoque_salonup.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      const hasFilters = filterCategoria !== 'all' || filterStatus !== 'all' || searchTerm;
      toast.success(`Estoque ${hasFilters ? 'filtrado' : 'completo'} exportado em ${ext.toUpperCase()}!`);
    } catch (error) {
      toast.error('Erro ao exportar estoque');
    } finally {
      setExporting(null);
    }
  };

  const filteredEstoque = estoque.filter(item => {
    const matchesSearch = 
      item.produto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.marca?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategoria = filterCategoria === 'all' || item.categoria === filterCategoria;
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesCategoria && matchesStatus;
  });

  const getStatusInfo = (status) => {
    switch(status) {
      case 'critico':
        return { label: 'Crítico', color: '#EF4444', bgColor: '#FEE2E2', icon: Warning };
      case 'baixo':
        return { label: 'Baixo', color: '#F59E0B', bgColor: '#FEF3C7', icon: ArrowDown };
      case 'normal':
        return { label: 'Normal', color: '#22C55E', bgColor: '#DCFCE7', icon: CheckCircle };
      case 'alto':
        return { label: 'Alto', color: '#3B82F6', bgColor: '#DBEAFE', icon: ArrowUp };
      default:
        return { label: status, color: '#71717A', bgColor: '#F4F4F5', icon: ChartBar };
    }
  };

  // Calculate summary stats
  const stats = {
    total: estoque.length,
    critico: estoque.filter(i => i.status === 'critico').length,
    baixo: estoque.filter(i => i.status === 'baixo').length,
    normal: estoque.filter(i => i.status === 'normal').length,
    valorTotal: estoque.reduce((acc, i) => acc + ((i.saldo_atual || 0) * (i.custo_unitario || 0)), 0)
  };

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="estoque-page">
      <Toaster position="top-right" richColors />
      <div className="salonup-container">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark">
              <Scissors size={22} weight="bold" />
            </div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          
          <button 
            onClick={() => navigate('/gestao/pacman')}
            data-testid="btn-back"
            className="salonup-btn-back"
          >
            <ArrowLeft size={18} weight="bold" />
            <span>Voltar para PAC MAN</span>
          </button>
        </header>

        {/* Breadcrumb */}
        <div className="mb-4 animate-fade-in opacity-0 delay-100" style={{ animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">·</span>
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/gestao')}>Gestão</span>
            <span className="mx-2">·</span>
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/gestao/pacman')}>PAC MAN</span>
            <span className="mx-2">·</span>
            <span className="text-[#18181B] font-medium">Estoque Atual</span>
          </p>
        </div>

        {/* Title Section */}
        <section className="mb-8 animate-fade-in-up opacity-0 delay-150" style={{ animationFillMode: 'forwards' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#40916C12' }}>
                <ChartBar size={28} weight="regular" style={{ color: '#40916C' }} />
              </div>
              <div>
                <h1 className="salonup-heading-lg">Estoque Atual</h1>
                <p className="salonup-text-sm mt-1">Visualização consolidada do saldo e situação dos produtos</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => handleExport('excel')}
                disabled={exporting !== null || estoque.length === 0}
                variant="outline"
                className="border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5 rounded-xl px-4 py-2.5 h-auto"
                data-testid="btn-export-excel"
              >
                <FileXls size={20} weight="bold" className="mr-2" />
                {exporting === 'excel' ? 'Exportando...' : 'Excel'}
              </Button>
              <Button 
                onClick={() => handleExport('pdf')}
                disabled={exporting !== null || estoque.length === 0}
                variant="outline"
                className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/5 rounded-xl px-4 py-2.5 h-auto"
                data-testid="btn-export-pdf"
              >
                <FilePdf size={20} weight="bold" className="mr-2" />
                {exporting === 'pdf' ? 'Exportando...' : 'PDF'}
              </Button>
            </div>
          </div>
        </section>

        {/* Stats Cards */}
        <section className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4 animate-fade-in-up opacity-0 delay-200" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card p-4">
            <p className="salonup-text-xs mb-1">Total de Itens</p>
            <p className="text-2xl font-semibold text-[#18181B]">{stats.total}</p>
          </div>
          <div className="salonup-card p-4 border-l-4 border-l-red-500">
            <p className="salonup-text-xs mb-1">Crítico</p>
            <p className="text-2xl font-semibold text-red-500">{stats.critico}</p>
          </div>
          <div className="salonup-card p-4 border-l-4 border-l-amber-500">
            <p className="salonup-text-xs mb-1">Baixo</p>
            <p className="text-2xl font-semibold text-amber-500">{stats.baixo}</p>
          </div>
          <div className="salonup-card p-4 border-l-4 border-l-green-500">
            <p className="salonup-text-xs mb-1">Normal</p>
            <p className="text-2xl font-semibold text-green-500">{stats.normal}</p>
          </div>
          <div className="salonup-card p-4 col-span-2 md:col-span-1">
            <p className="salonup-text-xs mb-1">Valor em Estoque</p>
            <p className="text-xl font-semibold text-[#1B4332]">{formatCurrency(stats.valorTotal)}</p>
          </div>
        </section>

        {/* Filters */}
        <section className="mb-6 animate-fade-in-up opacity-0 delay-250" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <Input 
                  placeholder="Buscar por nome, SKU ou marca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 border-[#E4E4E7] rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <div className="w-full md:w-56">
                <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                  <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl" data-testid="filter-categoria">
                    <Funnel size={18} className="mr-2 text-[#A1A1AA]" />
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {CATEGORIAS.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full md:w-44">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="critico">Crítico</SelectItem>
                    <SelectItem value="baixo">Baixo</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="alto">Alto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>

        {/* Estoque Table */}
        <section className="animate-fade-in-up opacity-0 delay-300" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <p className="salonup-text-body">Carregando estoque...</p>
              </div>
            ) : filteredEstoque.length === 0 ? (
              <div className="p-12 text-center">
                <ChartBar size={48} weight="light" className="mx-auto mb-4 text-[#A1A1AA]" />
                <p className="salonup-heading-md mb-2">Nenhum produto no estoque</p>
                <p className="salonup-text-sm">
                  {searchTerm || filterCategoria !== 'all' || filterStatus !== 'all'
                    ? 'Tente ajustar os filtros de busca'
                    : 'Cadastre produtos e registre movimentações para ver o estoque'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="estoque-table">
                  <thead className="bg-[#F7F6F4] border-b border-[#E4E4E7]">
                    <tr>
                      <th className="text-left p-4 salonup-label">Produto</th>
                      <th className="text-left p-4 salonup-label hidden md:table-cell">Categoria</th>
                      <th className="text-right p-4 salonup-label">Inicial</th>
                      <th className="text-right p-4 salonup-label">Entradas</th>
                      <th className="text-right p-4 salonup-label">Saídas</th>
                      <th className="text-right p-4 salonup-label">Saldo</th>
                      <th className="text-center p-4 salonup-label">Status</th>
                      <th className="text-right p-4 salonup-label hidden lg:table-cell">Comprar</th>
                      <th className="text-right p-4 salonup-label hidden lg:table-cell">Valor Est.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEstoque.map((item, index) => {
                      const statusInfo = getStatusInfo(item.status);
                      const StatusIcon = statusInfo.icon;
                      
                      return (
                        <tr 
                          key={item.id} 
                          className="border-b border-[#E4E4E7] hover:bg-[#F7F6F4]/50 transition-colors"
                          data-testid={`estoque-row-${index}`}
                        >
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-[#18181B]">{item.produto}</p>
                              <p className="text-xs text-[#71717A]">{item.sku} · {item.marca}</p>
                            </div>
                          </td>
                          <td className="p-4 hidden md:table-cell">
                            <span className="text-sm text-[#71717A]">{getCategoriaLabel(item.categoria)}</span>
                          </td>
                          <td className="p-4 text-right">
                            <span className="text-[#71717A]">{item.estoque_inicial}</span>
                          </td>
                          <td className="p-4 text-right">
                            <span className="text-green-600">+{item.entradas || 0}</span>
                          </td>
                          <td className="p-4 text-right">
                            <span className="text-red-500">-{item.saidas || 0}</span>
                          </td>
                          <td className="p-4 text-right">
                            <span className="font-semibold text-[#18181B]">{item.saldo_atual}</span>
                          </td>
                          <td className="p-4">
                            <div className="flex justify-center">
                              <span 
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}
                              >
                                <StatusIcon size={14} />
                                {statusInfo.label}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-right hidden lg:table-cell">
                            {item.sugestao_compra > 0 ? (
                              <span className="font-medium text-amber-600">{item.sugestao_compra}</span>
                            ) : (
                              <span className="text-[#71717A]">-</span>
                            )}
                          </td>
                          <td className="p-4 text-right hidden lg:table-cell">
                            <span className="text-[#71717A]">{formatCurrency(item.custo_estimado)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {filteredEstoque.length > 0 && (
            <p className="salonup-text-xs mt-4 text-center">
              {filteredEstoque.length} item{filteredEstoque.length !== 1 ? 's' : ''} no estoque
            </p>
          )}
        </section>

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in opacity-0" style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salão moderno</p>
        </footer>
      </div>
    </div>
  );
};

export default EstoqueAtual;
